import { z } from "zod";
import type { CardDef, RecipeDef, SetupOffer, StageDef } from "@reagent/sim";
import { totalUnits } from "@reagent/sim";
import { reachablePollutionReasons } from "./pollution";

/**
 * Runtime schemas for authored content.
 *
 * TypeScript already checks the *shape* of a stage at compile time. These
 * schemas exist for what it cannot check: that quantities are positive
 * integers, that a stage's edges name nodes that exist, that its deck names
 * cards that exist, and that its objective is reachable from its recipes.
 * Those are the mistakes an author actually makes, and they otherwise show up
 * as a rig that silently does nothing on tick 1.
 *
 * Everything is validated at module load, so a bad edit fails loudly at
 * startup instead of halfway through a run.
 */

const identifier = z.string().min(1).regex(/^[a-z0-9-]+$/, "ids are lower-kebab-case");

/** Matches `CardInstance` (`@reagent/sim`'s protocol) field-for-field. */
const cardInstanceSchema = z.object({
  instanceId: identifier,
  cardId: identifier,
  acquiredStageIndex: z.number().int().min(0),
  boundNodeId: identifier.nullable(),
  isContaminated: z.boolean(),
});

/** Integer unit counts, never zero or fractional — the engine is integer-only. */
const composition = z
  .record(z.string().min(1), z.number().int().positive())
  .refine((c) => Object.keys(c).length > 0, "a composition needs at least one species");

const gridPos = z.object({ x: z.number().int().min(0), y: z.number().int().min(0) });

const nodeKind = z.enum([
  "inputTank",
  "productHopper",
  "wasteBuyer",
  "reactor",
  "thermalRegulator",
  "separator",
  "phaseConverter",
  "catalyticBed",
  "incubator",
  "flowMover",
  "flowBuffer",
  "vent",
]);

const pipeMaterial = z.enum(["glass", "ptfeLined", "hastelloy"]);

const scriptedEventKind = z.enum(["powerSurge", "heatwave", "seismic"]);

const scriptedEventDef = z.object({
  turn: z.number().int().positive(),
  kind: scriptedEventKind,
});

/**
 * Spec 0027: Per-feed Budget vs. Premium contract. Budget carries impurities
 * (01-simulation-model.md §3), Premium carries an optional supply cap.
 * costCredits on both is non-negative; the cap is a positive multiple of the
 * tank's per-tick emission when present.
 */
const sourcingDef = z.object({
  budget: z.object({
    impurities: composition, // non-empty, validated by composition's own refine
    costCredits: z.number().int().nonnegative(),
  }),
  premium: z.object({
    supplyCap: z.number().int().positive().optional(),
    costCredits: z.number().int().nonnegative(),
  }),
});

// Deep equality check for two composition objects (same keys, same values).
const compositionsEqual = (a: Record<string, number>, b: Record<string, number>): boolean => {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  if (aKeys.length !== bKeys.length) return false;
  for (let i = 0; i < aKeys.length; i++) {
    const key = aKeys[i]!;
    if (key !== bKeys[i] || a[key]! !== b[key]!) return false;
  }
  return true;
};

export const recipeSchema = z
  .object({
    id: identifier,
    inputs: composition,
    outputs: composition,
    isExothermic: z.boolean(),
    heat: z.number().int().min(0),
    nodeKinds: z.array(nodeKind).min(1),
    boilingPoint: z.number().int().optional(),
    volatileSpecies: z.array(z.string().min(1)).min(1).optional(),
    holdDuration: z.number().int().positive().optional(),
    minTemperature: z.number().int().optional(),
    primarySpecies: z.array(z.string().min(1)).min(1).optional(),
  })
  .refine(
    (r) => (r.boilingPoint !== undefined && r.volatileSpecies !== undefined) || (r.boilingPoint === undefined && r.volatileSpecies === undefined),
    "boilingPoint and volatileSpecies must be present together or not at all",
  )
  .refine(
    (r) => !r.volatileSpecies || r.volatileSpecies.every((s) => s in r.inputs),
    "every volatileSpecies entry must appear in inputs",
  )
  .refine(
    (r) => !r.primarySpecies || r.primarySpecies.every((s) => s in r.inputs),
    "every primarySpecies entry must appear in inputs",
  )
  .refine(
    (r) =>
      (r.boilingPoint === undefined || r.nodeKinds.includes("phaseConverter")) &&
      (r.holdDuration === undefined || r.nodeKinds.includes("incubator")) &&
      (r.primarySpecies === undefined || r.nodeKinds.includes("separator")),
    "boilingPoint requires phaseConverter in nodeKinds; holdDuration requires incubator in nodeKinds; primarySpecies requires separator in nodeKinds",
  )
  .refine(
    (r) => r.minTemperature === undefined || r.holdDuration !== undefined,
    "minTemperature is only valid with holdDuration",
  )
  .refine(
    (r) => r.holdDuration !== undefined || (r.boilingPoint === undefined && r.primarySpecies === undefined) || compositionsEqual(r.outputs, r.inputs),
    "a phase change or separation conserves composition: outputs must deep-equal inputs; holdDuration recipes are exempt",
  );

const attachmentEffect = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("coolingJacket"), heatShedPerTick: z.number().int().positive() }),
  z.object({ kind: z.literal("ptfeLining") }),
  z.object({ kind: z.literal("burstDisk"), extraPressureTolerance: z.number().int().positive() }),
  z.object({ kind: z.literal("guardBed"), charges: z.number().int().positive() }),
  z.object({ kind: z.literal("insulation"), extraCriticalTemp: z.number().int().positive() }),
  z.object({ kind: z.literal("surgeDrum"), extraBufferCapacity: z.number().int().positive() }),
  z.object({ kind: z.literal("lid") }),
]);

const directiveEffect = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("flashFreeze") }),
  z.object({ kind: z.literal("stirVat") }),
  z.object({ kind: z.literal("lowFire"), maxMultiple: z.number().int().positive() }),
  z.object({ kind: z.literal("solventFlush") }),
  z.object({ kind: z.literal("nitrogenPurge") }),
  z.object({ kind: z.literal("emergencyFlush") }),
  z.object({ kind: z.literal("patchWeld"), hp: z.number().int().positive() }),
  z.object({ kind: z.literal("catalystRecharge") }),
  z.object({ kind: z.literal("overtime"), extraKw: z.number().int().positive() }),
  z.object({ kind: z.literal("rushOrder"), cards: z.number().int().positive() }),
  z.object({ kind: z.literal("fumigate") }),
  z.object({ kind: z.literal("scrubTile") }),
]);

/**
 * Spec 0028: the six Phase 1 facility installs, one answer per
 * `03-hazards.md` §3 consequence. Mirrors `@reagent/sim`'s engine-side
 * `FacilityMitigation` union field-for-field — see that type's doc comment
 * for what each kind answers.
 */
const facilityMitigation = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("hazmatAbsorbent") }), // §3.1
  z.object({ kind: z.literal("surgeProtector") }), // §3.2
  z.object({ kind: z.literal("emergencyPower"), kw: z.number().int().positive() }), // §3.2
  z.object({ kind: z.literal("pestControl") }), // §3.3
  z.object({ kind: z.literal("hvacChiller") }), // §3.4 heatwave
  z.object({ kind: z.literal("seismicMounts") }), // §3.4 seismic
]);

const hazardEffect = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("powerPenalty"), kw: z.number().int().positive() }),
  z.object({ kind: z.literal("drawPenalty"), cards: z.number().int().positive() }),
  z.object({ kind: z.literal("directiveSurcharge"), kw: z.number().int().positive() }),
  z.object({ kind: z.literal("maintenanceFee"), credits: z.number().int().positive() }),
  z.object({ kind: z.literal("tileLock") }),
  z.object({ kind: z.literal("handSlotLock") }),
  z.object({ kind: z.literal("powerDrain") }),
  z.object({ kind: z.literal("infestation") }),
  z.object({ kind: z.literal("pipeStress"), percentOfMaxHp: z.number().int().positive() }),
]);

export const cardSchema = z
  .object({
    id: identifier,
    name: z.string().min(1),
    category: z.enum(["hardware", "metaUtility", "directive", "attachment", "hazard"]),
    costKw: z.number().int().min(0),
    deploysNode: nodeKind.optional(),
    deploysNodeConfig: z
      .object({
        bufferCapacity: z.number().int().positive().optional(),
        criticalTemp: z.number().int().positive().optional(),
        // thermalRegulator only (spec 0022): signed, unlike the two above —
        // a chiller's ΔT is negative.
        deltaTemp: z.number().int().optional(),
        // flowMover only (spec 0024): extra buffer depth this mover may push past
        // downstream — see EngineNode.pumpOverride in packages/sim.
        pumpOverride: z.number().int().nonnegative().optional(),
        // Spec 0031: recipe ids this node is bound to.
        recipeIds: z.array(identifier).optional(),
      })
      .optional(),
    deploysPipe: pipeMaterial.optional(),
    /** Bound Hardware (§2.1): the operational cards installing this device injects. */
    injectsCards: z.record(identifier, z.number().int().positive()).optional(),
    attachment: attachmentEffect.optional(),
    attachesToNodeKind: nodeKind.optional(),
    directive: directiveEffect.optional(),
    hazard: hazardEffect.optional(),
    /** Spec 0028. `metaUtility` only, and mutually exclusive with `directive` — see the refinement below. */
    facility: facilityMitigation.optional(),
    isPlayable: z.boolean().optional(),
    clearsOnPlay: z.boolean().optional(),
  })
  // A card whose category promises an effect it doesn't carry is playable and
  // inert — the worst kind of content bug, because nothing errors.
  .refine(
    (c) => c.category !== "hardware" || Boolean(c.deploysNode ?? c.deploysPipe),
    "a hardware card must deploy a node or a pipe",
  )
  .refine((c) => c.category !== "attachment" || Boolean(c.attachment), "an attachment card must carry an attachment effect")
  .refine((c) => c.category !== "directive" || Boolean(c.directive), "a directive card must carry a directive effect")
  .refine((c) => c.category !== "hazard" || Boolean(c.hazard), "a hazard card must carry a hazard effect")
  // metaUtility carries exactly one of the two payloads that predate the
  // category split (spec 0020's directive recategorisation) and the one this
  // spec adds (facility) — never both, never neither.
  .refine(
    (c) => c.category !== "metaUtility" || Boolean(c.directive) !== Boolean(c.facility),
    "a metaUtility card must carry exactly one of a directive or a facility effect",
  )
  .refine(
    (c) => !c.deploysNodeConfig || Boolean(c.deploysNode),
    "node configuration only means something on a card that deploys a node",
  )
  .refine(
    (c) => !c.injectsCards || c.category === "hardware",
    "injectsCards only means something on a hardware card",
  )
  .refine(
    (c) => !c.attachesToNodeKind || c.category === "attachment",
    "attachesToNodeKind only means something on an attachment card",
  )
  .refine(
    (c) => !c.facility || c.category === "metaUtility",
    "facility only means something on a metaUtility card",
  )
  .refine(
    (c) => (c.isPlayable === undefined && c.clearsOnPlay === undefined) || c.category === "hazard",
    "isPlayable/clearsOnPlay only mean something on a hazard card",
  );

/** Exported so `stages.test.ts` can exercise the sourcing refinements directly (spec 0027). */
export const nodeSchema = z
  .object({
    id: identifier,
    kind: nodeKind,
    pos: gridPos,
    rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).optional(),
    bufferCapacity: z.number().int().positive().optional(),
    criticalTemp: z.number().int().positive().optional(),
    deltaTemp: z.number().int().optional(),
    feedTemperature: z.number().int().optional(),
    pumpOverride: z.number().int().nonnegative().optional(),
    emits: composition.optional(),
    sourcing: sourcingDef.optional(),
    // Spec 0031: recipe ids this node is bound to.
    recipeIds: z.array(identifier).optional(),
  })
  .refine((n) => n.kind === "inputTank" || n.sourcing === undefined, "only an input tank has sourcing")
  .refine((n) => n.kind === "thermalRegulator" || n.deltaTemp === undefined, "only a thermalRegulator has a deltaTemp")
  .refine((n) => n.kind === "inputTank" || n.feedTemperature === undefined, "only an input tank has a feedTemperature")
  .refine((n) => n.kind === "flowMover" || n.pumpOverride === undefined, "only a flowMover has a pumpOverride")
  .refine(
    (n) =>
      n.sourcing?.premium.supplyCap === undefined ||
      (n.emits !== undefined && n.sourcing.premium.supplyCap > 0 && n.sourcing.premium.supplyCap % totalUnits(n.emits) === 0),
    "supplyCap must be a positive whole multiple of the tank's per-tick emission",
  );

/** One offer in Phase 1's setup catalogue (spec 0017). Matches `SetupOffer` field-for-field. */
const setupOfferSchema = z.object({ cardId: identifier, costCredits: z.number().int().min(0) });

const edgeSchema = z.object({
  id: identifier,
  fromNodeId: identifier,
  toNodeId: identifier,
  path: z.array(gridPos).min(1),
  material: pipeMaterial.optional(),
  // Matches EdgeDef.phaseRole (engine/definitions.ts) exactly — the engine's
  // authored type carries no `null`, only its projected EdgeState does.
  phaseRole: z.enum(["vapour", "liquid"]).optional(),
  separationRole: z.enum(["main", "waste"]).optional(),
});

export const stageSchema = z
  .object({
    id: identifier,
    name: z.string().min(1),
    blurb: z.string().min(1),
    cols: z.number().int().positive().max(16),
    rows: z.number().int().positive().max(10),
    nodes: z.array(nodeSchema).min(1),
    edges: z.array(edgeSchema),
    recipes: z.array(recipeSchema).min(1),
    objective: z.object({
      targetComposition: composition,
      minPurity: z.number().int().min(0).max(100),
    }),
    energyBudgetKw: z.number().int().positive(),
    handSize: z.number().int().positive(),
    ticksPerTurn: z.number().int().positive(),
    maxTurns: z.number().int().positive(),
    corrosiveSpecies: z.array(z.string().min(1)),
    startingDeck: z.array(cardInstanceSchema).min(1),
    scriptedEvents: z.array(scriptedEventDef).optional(),
    pollutionCards: z
      .object({
        leak: identifier.optional(),
        overheat: identifier.optional(),
        sludge: identifier.optional(),
        catalystDead: identifier.optional(),
        spill: identifier.optional(),
        vermin: identifier.optional(),
        powerOutage: identifier.optional(),
        structuralShear: identifier.optional(),
        burst: identifier.optional(),
      })
      .optional(),
    /**
     * Phase 1's own budget and shop (`00-pillars.md` §3, spec 0017). Optional
     * and additive, like `scriptedEvents` above: omitted means `0` and `[]`,
     * which is every stage this package ships today (`0037` rebalances them).
     */
    setupBudgetCredits: z.number().int().min(0).optional(),
    setupCatalogue: z.array(setupOfferSchema).optional(),
    /** Spec 0067: omitted means Play-phase building is closed. Matches `LiveBuildRule` field-for-field. */
    liveBuild: z
      .object({
        installLabourKw: z.number().int().min(0),
        dismantleLabourKw: z.number().int().min(0),
        moveLabourKw: z.number().int().min(0),
        moveCostCredits: z.number().int().min(0),
        playRefundPct: z.number().int().min(0).max(100),
        cleanFeeCredits: z.number().int().min(0),
        spillFreeMovesPerTurn: z.number().int().min(0),
      })
      .optional(),
  })
  .superRefine((stage, ctx) => {
    const fail = (message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message });

    const ids = new Set<string>();
    for (const node of stage.nodes) {
      if (ids.has(node.id)) fail(`duplicate node id: ${node.id}`);
      ids.add(node.id);
      if (node.pos.x >= stage.cols || node.pos.y >= stage.rows) {
        fail(`node ${node.id} sits off the board at ${node.pos.x},${node.pos.y}`);
      }
      if (node.kind === "inputTank" && !node.emits) fail(`input tank ${node.id} emits nothing`);
      if (node.kind !== "inputTank" && node.emits) fail(`${node.id} is not an input tank but emits`);
    }

    const occupied = new Set<string>();
    for (const node of stage.nodes) {
      const key = `${node.pos.x},${node.pos.y}`;
      if (occupied.has(key)) fail(`two nodes share tile ${key}`);
      occupied.add(key);
    }

    const edgeIds = new Set<string>();
    for (const edge of stage.edges) {
      if (edgeIds.has(edge.id)) fail(`duplicate edge id: ${edge.id}`);
      edgeIds.add(edge.id);
      if (!ids.has(edge.fromNodeId)) fail(`edge ${edge.id} starts at unknown node ${edge.fromNodeId}`);
      if (!ids.has(edge.toNodeId)) fail(`edge ${edge.id} ends at unknown node ${edge.toNodeId}`);

      // The renderer draws the pipe along this path, so it has to actually
      // start and end where the machines are.
      const from = stage.nodes.find((n) => n.id === edge.fromNodeId);
      const to = stage.nodes.find((n) => n.id === edge.toNodeId);
      const head = edge.path[0];
      const tail = edge.path[edge.path.length - 1];
      if (from && head && (head.x !== from.pos.x || head.y !== from.pos.y)) {
        fail(`edge ${edge.id} does not start on node ${from.id}`);
      }
      if (to && tail && (tail.x !== to.pos.x || tail.y !== to.pos.y)) {
        fail(`edge ${edge.id} does not end on node ${to.id}`);
      }

      // Every consecutive pair of tiles must be orthogonally adjacent — the
      // renderer walks the path one grid step at a time and has no way to
      // draw a diagonal, a skip, or a stall.
      for (let i = 1; i < edge.path.length; i++) {
        const prev = edge.path[i - 1]!;
        const curr = edge.path[i]!;
        const dx = Math.abs(curr.x - prev.x);
        const dy = Math.abs(curr.y - prev.y);
        if (!((dx === 1 && dy === 0) || (dx === 0 && dy === 1))) {
          fail(`edge ${edge.id} path is not orthogonally contiguous between (${prev.x},${prev.y}) and (${curr.x},${curr.y})`);
        }
      }
    }

    if (!stage.nodes.some((n) => n.kind === "productHopper")) fail("a stage needs somewhere to collect product");
    if (!stage.nodes.some((n) => n.kind === "inputTank")) fail("a stage needs at least one feed");

    // The objective has to be makeable: every target species must be produced
    // by some recipe, or arrive straight from a feed.
    const produced = new Set<string>();
    for (const recipe of stage.recipes) for (const key of Object.keys(recipe.outputs)) produced.add(key);
    for (const node of stage.nodes) for (const key of Object.keys(node.emits ?? {})) produced.add(key);
    for (const key of Object.keys(stage.objective.targetComposition)) {
      if (!produced.has(key)) fail(`nothing in this stage produces ${key}`);
    }

    // Likewise every recipe input must come from somewhere.
    for (const recipe of stage.recipes) {
      for (const key of Object.keys(recipe.inputs)) {
        if (!produced.has(key)) fail(`recipe ${recipe.id} needs ${key}, which nothing supplies`);
      }
    }

    // Spec 0031: validate node-bound recipes (criterion 7).
    const recipeById = new Map(stage.recipes.map((r) => [r.id, r]));
    for (const node of stage.nodes) {
      if (node.recipeIds) {
        for (const recipeId of node.recipeIds) {
          const recipe = recipeById.get(recipeId);
          if (!recipe) {
            fail(`node ${node.id} is bound to unknown recipe ${recipeId}`);
          } else if (!recipe.nodeKinds.includes(node.kind)) {
            fail(`node ${node.id} is bound to recipe ${recipeId}, whose nodeKinds excludes ${node.kind}`);
          }
        }
      }
    }
  });

/**
 * Parses and returns the value, keeping the authored literal type rather than
 * zod's inferred one — the engine's own interfaces stay the source of truth
 * for shape, and these schemas add the checks a type cannot make.
 */
export function validateRecipe(recipe: RecipeDef): RecipeDef {
  recipeSchema.parse(recipe);
  return recipe;
}

export function validateCard(card: CardDef): CardDef {
  cardSchema.parse(card);
  return card;
}

export function validateStage(stage: StageDef, cards: readonly CardDef[]): StageDef {
  stageSchema.parse(stage);

  const cardIds = new Set(cards.map((c) => c.id));
  const missing = [
    ...stage.startingDeck.filter((ci) => !cardIds.has(ci.cardId)).map((ci) => ci.cardId),
    ...Object.values(stage.pollutionCards ?? {}).filter((id) => id && !cardIds.has(id)),
    ...(stage.setupCatalogue ?? []).filter((offer) => !cardIds.has(offer.cardId)).map((offer) => offer.cardId),
  ];
  if (missing.length > 0) {
    throw new Error(`stage ${stage.id} names cards that do not exist: ${[...new Set(missing)].join(", ")}`);
  }

  // A pollutionCards entry that names a real card of the wrong category is
  // playable and does nothing when the hazard fires — the same silent-inert
  // failure the missing-id check exists for, one layer deeper.
  const cardsById = new Map(cards.map((c) => [c.id, c]));
  const wrongCategory = Object.entries(stage.pollutionCards ?? {}).filter(([, id]) => {
    if (!id) return false;
    const card = cardsById.get(id);
    return card !== undefined && card.category !== "hazard";
  });
  if (wrongCategory.length > 0) {
    const detail = wrongCategory
      .map(([reason, id]) => `${reason} -> ${id} (${cardsById.get(id!)!.category})`)
      .join(", ");
    throw new Error(`stage ${stage.id} pollutionCards names a non-hazard card: ${detail}`);
  }

  // Phase 1's catalogue sells hardware, or (spec 0028) a metaUtility card
  // carrying a facility payload — an offer naming a directive, attachment,
  // hazard, or directive-payload metaUtility card is buyable and inert the
  // moment Play (or Setup's installHardware) tries to deploy it, the same
  // shape of bug as above.
  const wrongCatalogueCategory = (stage.setupCatalogue ?? []).filter((offer) => {
    const card = cardsById.get(offer.cardId);
    if (card === undefined) return false;
    if (card.category === "hardware") return false;
    if (card.category === "metaUtility" && card.facility !== undefined) return false;
    return true;
  });
  if (wrongCatalogueCategory.length > 0) {
    const detail = wrongCatalogueCategory
      .map((offer) => `${offer.cardId} (${cardsById.get(offer.cardId)!.category})`)
      .join(", ");
    throw new Error(`stage ${stage.id} setupCatalogue names a card that is neither hardware nor a facility mitigation: ${detail}`);
  }

  // Spec 0068: a catalogue card's own recipe binding is the default its installed
  // node takes, so it has to name a recipe this stage has and the card's node
  // kind can run — the same two checks 0031 applies to a pre-placed node.
  const stageRecipes = new Map(stage.recipes.map((r) => [r.id, r]));
  for (const offer of stage.setupCatalogue ?? []) {
    const card = cardsById.get(offer.cardId)!;
    for (const recipeId of card.deploysNodeConfig?.recipeIds ?? []) {
      const recipe = stageRecipes.get(recipeId);
      if (!recipe) {
        throw new Error(`stage ${stage.id} setupCatalogue card ${card.id} is bound to unknown recipe ${recipeId}`);
      }
      if (card.deploysNode === undefined || !recipe.nodeKinds.includes(card.deploysNode)) {
        throw new Error(
          `stage ${stage.id} setupCatalogue card ${card.id} is bound to recipe ${recipeId}, whose nodeKinds excludes ${card.deploysNode ?? "no node"}`,
        );
      }
    }
  }

  // Spec 0027: the cheapest mandatory sourcing contracts must fit in the stage's
  // setup budget, so the player always has a path to leaving setup.
  const sourcedTanks = stage.nodes.filter((n) => n.kind === "inputTank" && n.sourcing);
  const minMandatory = sourcedTanks.reduce(
    (sum, n) => sum + Math.min(n.sourcing!.budget.costCredits, n.sourcing!.premium.costCredits),
    0,
  );
  if (minMandatory > (stage.setupBudgetCredits ?? 0)) {
    throw new Error(
      `stage ${stage.id}'s cheapest mandatory sourcing contracts (${minMandatory}₵) exceed its setupBudgetCredits (${stage.setupBudgetCredits ?? 0}₵)`,
    );
  }

  // Spec 0028: the shop guarantee covers scripted events only. For every kind
  // present in scriptedEvents, at least one matching facility mitigation card
  // must be on sale. The mapping: powerSurge → surgeProtector or emergencyPower;
  // heatwave → hvacChiller; seismic → seismicMounts. Emergent hazards
  // (contamination, vermin) are not checked — offering their answer is an
  // authoring choice (Resolved Question 3).
  const scriptedEventKinds = new Set((stage.scriptedEvents ?? []).map((e) => e.kind));
  if (scriptedEventKinds.size > 0) {
    const catalogueCards = (stage.setupCatalogue ?? []).map((offer) => cardsById.get(offer.cardId)!);
    const catalogueFacilityKinds = new Set<string>();
    for (const card of catalogueCards) {
      if (card.facility) {
        catalogueFacilityKinds.add(card.facility.kind);
      }
    }

    const missingAnswers: string[] = [];
    for (const kind of scriptedEventKinds) {
      if (kind === "powerSurge") {
        if (!catalogueFacilityKinds.has("surgeProtector") && !catalogueFacilityKinds.has("emergencyPower")) {
          missingAnswers.push("powerSurge");
        }
      } else if (kind === "heatwave") {
        if (!catalogueFacilityKinds.has("hvacChiller")) {
          missingAnswers.push("heatwave");
        }
      } else if (kind === "seismic") {
        if (!catalogueFacilityKinds.has("seismicMounts")) {
          missingAnswers.push("seismic");
        }
      }
    }

    if (missingAnswers.length > 0) {
      throw new Error(
        `stage ${stage.id} authors scripted events but has no answer on sale for: ${missingAnswers.join(", ")}`,
      );
    }
  }

  // Spec 0029: a stage that can actually trigger a pollution reason must map
  // it, closing GAPS.md Q14 — pollutionCards was previously an opt-in
  // curriculum choice with no floor. Over-mapping (a reason mapped but never
  // reachable) is still fine; only a gap is an error.
  const reachable = reachablePollutionReasons(stage, cards);
  const mapped = new Set(Object.keys(stage.pollutionCards ?? {}));
  const unmapped = [...reachable].filter((reason) => !mapped.has(reason)).sort();
  if (unmapped.length > 0) {
    throw new Error(`stage ${stage.id} can trigger unmapped pollution reasons: ${unmapped.join(", ")}`);
  }

  return stage;
}

/**
 * Spec 0017 open question 1's confirmed authoring rule (2026-09-19): needs
 * playtest confirmation of the multiplier itself, not the mechanism. Exported
 * for stage authors (spec 0037) populating a real setupCatalogue.
 */
export function setupOfferFor(card: CardDef): SetupOffer {
  return { cardId: card.id, costCredits: card.costKw * 5 };
}
